import { useEffect, useRef, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { EmptyState } from '@/shared/layout/EmptyState'
import { ErrorState } from '@/shared/layout/ErrorState'
import { usePageTitle } from '@/shared/layout/page-title'
import { PageHeader } from '@/shared/layout/PageHeader'
import { PageSkeleton } from '@/shared/layout/Skeleton'
import type { Icon } from '@/shared/registry'
import { PageContainer } from '@/shared/ui'

import type { RecordState } from './use-record'

/**
 * A record page's loading, missing and error states, and the page itself once the record is
 * there. When it cannot be shown, the header names the record's type (`title`), such as "User".
 * A missing record's heading and the browser tab's title say it was not found (WCAG 2.4.2).
 */
export function RecordView<T>(props: {
  readonly state: RecordState<T> & { readonly reload: () => void }
  readonly icon: Icon
  readonly title: string
  readonly children: (record: T, version: string | null) => ReactNode
}) {
  const intl = useIntl()
  const { state } = props
  useHeadingFocusAfterReload(state.status === 'ready' ? state.record : state.status === 'loading' ? null : state.status)
  const notFound = intl.formatMessage({ id: 'details.record.notFound' })
  usePageTitle(state.status === 'missing' ? notFound : undefined)

  if (state.status === 'ready') {
    return props.children(state.record, state.version)
  }

  // No heading while loading, so focus after navigation waits for the record's own.
  if (state.status === 'loading') {
    return (
      <PageContainer>
        <PageSkeleton label={intl.formatMessage({ id: 'details.record.loading' })} />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      {state.status === 'missing' ? (
        <PageHeader icon={props.icon} eyebrow={props.title} title={notFound} />
      ) : (
        <PageHeader icon={props.icon} title={props.title} />
      )}
      {state.status === 'missing' && <EmptyState>{intl.formatMessage({ id: 'details.record.missing' })}</EmptyState>}
      {state.status === 'error' && (
        <ErrorState title={intl.formatMessage({ id: 'details.record.error' })} onRetry={state.reload} />
      )}
    </PageContainer>
  )
}

/**
 * A reload can take away the control that held focus, such as an action a dialog's change no longer
 * offers, or the whole page while it loads again or when it ends missing or failed. Focus then goes
 * to the page's `h1`, never the page body. Focus the user moved off a control that is still shown
 * is left alone. `settled` is what is shown once loaded: the record, or the state it ended in.
 */
function useHeadingFocusAfterReload(settled: unknown) {
  const lastFocused = useRef<Element | null>(null)
  const shown = useRef<unknown>(null)

  useEffect(() => {
    lastFocused.current = document.activeElement
    const onFocusIn = (event: FocusEvent) => {
      lastFocused.current = event.target instanceof Element ? event.target : null
    }
    document.addEventListener('focusin', onFocusIn)
    return () => document.removeEventListener('focusin', onFocusIn)
  }, [])

  useEffect(() => {
    if (settled === null) {
      return
    }
    const previous = shown.current
    shown.current = settled
    const active = document.activeElement
    const isLost = active === null || active === document.body || !active.isConnected
    const removed = lastFocused.current
    if (previous === null || !isLost || removed === null || removed.isConnected) {
      return
    }
    ;(document.querySelector('main') ?? document).querySelector<HTMLElement>('h1')?.focus()
  }, [settled])
}
